import React, { useState, useMemo } from 'react';
import {
  Star,
  Search,
  Filter,
  Eye,
  EyeOff,
  Flag,
  CheckCircle2,
  AlertTriangle,
  User,
  Wrench,
  ThumbsUp,
} from 'lucide-react';
import type { AdminReviewItem } from '../../types';

interface AdminReviewsSectionProps {
  reviews: AdminReviewItem[];
  onModerate: (reviewId: string, action: 'hide' | 'restore' | 'flag', reason?: string) => Promise<void>;
  isProcessing: boolean;
}

export const AdminReviewsSection: React.FC<AdminReviewsSectionProps> = ({
  reviews,
  onModerate,
  isProcessing,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [modalData, setModalData] = useState<{
    isOpen: boolean;
    review: AdminReviewItem | null;
    action: 'hide' | 'restore' | 'flag';
  }>({
    isOpen: false,
    review: null,
    action: 'hide',
  });
  const [reason, setReason] = useState('');

  const filteredReviews = useMemo(() => {
    return reviews.filter((r) => {
      const comm = (r.comment || '').toLowerCase();
      const cust = (r.customerName || '').toLowerCase();
      const pro = (r.professionalName || '').toLowerCase();
      const term = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm || comm.includes(term) || cust.includes(term) || pro.includes(term);

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'visible' && !r.isHidden && !r.isFlagged) ||
        (statusFilter === 'hidden' && r.isHidden) ||
        (statusFilter === 'flagged' && r.isFlagged);

      const matchesRating =
        ratingFilter === 'all' || r.rating.toString() === ratingFilter;

      return matchesSearch && matchesStatus && matchesRating;
    });
  }, [reviews, searchTerm, statusFilter, ratingFilter]);

  const handleOpenModal = (review: AdminReviewItem, action: 'hide' | 'restore' | 'flag') => {
    setModalData({ isOpen: true, review, action });
    setReason(
      action === 'hide'
        ? 'Review hidden due to abusive language or policy violation.'
        : action === 'flag'
        ? 'Flagged for moderation review.'
        : 'Review restored.'
    );
  };

  const handleConfirm = async () => {
    if (!modalData.review) return;
    await onModerate(modalData.review.id, modalData.action, reason);
    setModalData({ isOpen: false, review: null, action: 'hide' });
  };

  return (
    <div id="admin-reviews-section" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-5 rounded-2xl">
        <div>
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
            <span>Ratings & Reviews Moderation Desk ({reviews.length})</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Audit customer feedback and ratings. Hide spam or abusive language while maintaining rating trust.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 font-semibold border border-emerald-500/20">
            {reviews.filter((r) => !r.isHidden).length} Public
          </span>
          <span className="px-2.5 py-1 rounded-lg bg-rose-500/10 text-rose-300 font-semibold border border-rose-500/20">
            {reviews.filter((r) => r.isHidden).length} Hidden
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search comment, customer, or artisan..."
            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
          />
        </div>

        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="all">All Moderation Statuses</option>
            <option value="visible">Public & Visible</option>
            <option value="hidden">Hidden by Admin</option>
            <option value="flagged">Flagged</option>
          </select>
        </div>

        <div>
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="all">All Star Ratings</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
        </div>
      </div>

      {/* Reviews Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredReviews.length === 0 ? (
          <div className="md:col-span-2 py-12 text-center bg-slate-900 border border-slate-800 rounded-2xl">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2 opacity-80" />
            <p className="text-xs font-semibold text-slate-300">No reviews found matching the filter.</p>
          </div>
        ) : (
          filteredReviews.map((r) => (
            <div
              key={r.id}
              className={`p-5 bg-slate-900 border rounded-2xl space-y-3 transition-all ${
                r.isHidden
                  ? 'border-rose-500/30 bg-rose-950/10'
                  : r.isFlagged
                  ? 'border-amber-500/30 bg-amber-950/10'
                  : 'border-slate-800'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-1 text-amber-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                      key={i}
                      className={`w-3.5 h-3.5 ${
                        i < r.rating ? 'fill-amber-400' : 'text-slate-600'
                      }`}
                    />
                  ))}
                  <span className="text-xs font-bold text-white ml-1.5">{r.rating}.0</span>
                </div>

                <div className="flex items-center gap-1.5">
                  {r.isHidden && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-rose-500/20 text-rose-300">
                      Hidden
                    </span>
                  )}
                  {r.isFlagged && (
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-amber-500/20 text-amber-300">
                      Flagged
                    </span>
                  )}
                </div>
              </div>

              <p className="text-xs text-slate-200 leading-relaxed italic">
                "{r.comment}"
              </p>

              <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between text-[11px] text-slate-400">
                <div>
                  By <strong className="text-slate-200">{r.customerName}</strong> for <strong className="text-emerald-300">{r.professionalName}</strong>
                </div>

                <div className="flex items-center gap-1.5">
                  {r.isHidden ? (
                    <button
                      onClick={() => handleOpenModal(r, 'restore')}
                      className="p-1.5 bg-slate-800 hover:bg-slate-700 text-emerald-400 rounded-lg"
                      title="Restore to Public"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleOpenModal(r, 'hide')}
                      className="p-1.5 bg-slate-800 hover:bg-rose-900/40 text-slate-400 hover:text-rose-300 rounded-lg"
                      title="Hide from Public View"
                    >
                      <EyeOff className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Action Modal */}
      {modalData.isOpen && modalData.review && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold text-white capitalize">
              {modalData.action} Review
            </h3>

            <p className="text-xs text-slate-300 italic bg-slate-950 p-3 rounded-xl">
              "{modalData.review.comment}"
            </p>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Moderation Reason:
              </label>
              <textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setModalData({ isOpen: false, review: null, action: 'hide' })}
                disabled={isProcessing}
                className="px-4 py-2 bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isProcessing || !reason.trim()}
                className="px-4 py-2 bg-emerald-500 text-slate-950 text-xs font-bold rounded-xl"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
